/**
 * Copyright (c) 2010-2022 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { Injectable, Injector } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanLoad, Route, Router, RouterStateSnapshot } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Observable, of, zip } from 'rxjs';
import { AuthState } from '../../security/state/authState.state';
import { Select, Store } from '@ngxs/store';
import { concatMap, filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { LoggerService } from '../../../modules/shared/services/logger.service';
import { LoginMethodType, RequestedRoute } from '../../security/state/authState.model';
import { RedirectToLogin } from '../../security/state/authState.actions';
import { OAuthService } from 'angular-oauth2-oidc';
import { DashGisUtil } from '../../../modules/shared/dash-gis-util';
import { IsAuthenticatedTokens } from '../../security/is-authenticated-tokens';
import { IsAuthenticated } from '../../security/services/isAuthenticated';

@Injectable()
export class AuthenticatedGuard implements CanActivate, CanLoad {
    @Select(AuthState.loginMethods) enabledMethods$: Observable<string[]>;
    @Select(AuthState.activeMethod) activeMethod$: Observable<LoginMethodType>;
    @Select(AuthState.token) private token$: Observable<string>;
    @Select(AuthState.authenticationInProgress) private authenticationInProgress$: Observable<boolean>;

    constructor(
        private logger: LoggerService,
        private jwtService: JwtHelperService,
        private oauthService: OAuthService,
        private router: Router,
        private store: Store,
        private injector: Injector
    ) {}

    canLoad(route: Route): Observable<boolean> {
        const targetUrl = this.router.getCurrentNavigation().extractedUrl.toString();
        const requested = DashGisUtil.buildRequestedRoute(targetUrl);
        return this.checkAuthenticatedState(requested);
    }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        const requested = DashGisUtil.buildRequestedRoute(state.url);
        return this.checkAuthenticatedState(requested);
    }

    private checkAuthenticatedState(requested: RequestedRoute) {
        if (requested.url === '/auth/disclaimer') {
            return of(true);
        }
        // criteria:
        return this.store.select(AuthState.authenticationInProgress).pipe(
            withLatestFrom(this.store.select(AuthState.isStartingUp)),
            //  filter out if login / token validation is in progress
            filter(([ inProgress, startingUp ]) => !inProgress || startingUp),

            // get both the list of enabled methods, which we will use later, and the active method
            concatMap(() => zip(this.enabledMethods$, this.activeMethod$)),

            // if there is an active method, only check that, otherwise check all enabled methods
            map(([ methods, activeMethod ]) => (activeMethod === 'none' ? methods : [ activeMethod ])),

            // switch path depending on result
            switchMap((methods) => {
                if (methods && methods.length > 0) {
                    return this.checkAuthenticatedServices$(methods);
                } else {
                    return of(false);
                }
            }),

            // if not authenticated, head to the login page
            tap((canActivate) => {
                if (!canActivate) {
                    setTimeout(() => this.store.dispatch(new RedirectToLogin(requested)), 0);
                }
            })
        );
    }

    private checkAuthenticatedServices$(validMethods: string[]) {
        return of(validMethods).pipe(
            // check valid methods
            mergeMap((methods) => {
                const authStreams = methods.map((method) => {
                    const service: IsAuthenticated = this.injector.get(IsAuthenticatedTokens.get(method));
                    return service.csIsAuthenticated$();
                });
                return zip(...authStreams);
            }),

            // any one successful method is sufficient
            map((authenticated) => authenticated.some((b) => b))
        );
    }
}
